import React, { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { HeroBackgroundStrokes } from '../components/common/SyncratikLogo';

// Lazy load the heavy components
const IntegrationCarousel = lazy(() => import('../components/homepage/IntegrationCarousel'));
const VoicesSection = lazy(() => import('../components/homepage/VoicesSection'));
const ScriptShowcase = lazy(() => import('../components/homepage/ScriptShowcase'));
const LottieSection = lazy(() => import('../components/homepage/LottieSection'));

const handleLoginClick = () => {
    // if (hasValidSession()) {
    //     // Determine dashboard URL based on environment
    //     const dashboardUrl = getDashboardUrl();
    //     window.location.href = dashboardUrl;
    // } else {
    //     setIsLoginOpen(true);
    // }

    // Determine dashboard URL based on environment
    const dashboardUrl = getDashboardUrl();
    window.location.href = dashboardUrl;
};

const getDashboardUrl = () => {
    // Check if running locally (development)
    const isLocal = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === 'local.dashboard.syncratik' ||
        process.env.NODE_ENV === 'development';

    if (isLocal) {
        return 'http://localhost:7287';
    } else {
        return 'https://dashboard.syncratik.com/';
    }
};

const HomePage = ({ openModal }) => {
    return (
        <div className="bg-gray-10 min-h-screen relative">
            {/* Background with logo strokes - repeating pattern with fixed height */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <HeroBackgroundStrokes
                    className="absolute inset-0 w-full"
                    style={{
                        height: '100%',
                        backgroundImage: 'url(/assets/logo-strokes.png)',
                        backgroundRepeat: 'repeat-y',
                        backgroundSize: '100% 2216px',
                        backgroundPosition: 'center top'
                    }}
                />
            </div>

            {/* Hero Section */}
            <section className="relative min-h-[70vh] flex items-center">
                <div className="container-content py-16 relative z-10">
                    <div className="text-center md:text-left mx-auto md:mx-0">
                        {/* Main Headline */}
                        <h1 className="text-h1 md:text-h0 text-gray-95 mb-3 max-w-4xl">
                            AI calls that sound lifelike<br />
                            Train agents in days, not months
                        </h1>

                        {/* Subtitle */}
                        <p className="text-subheading text-gray-90 mb-7 max-w-md mx-auto md:mx-0">
                            Syncratik provides no-code solutions so businesses can start using AI with little overhead.
                        </p>

                        {/* CTA Button */}
                        <button onClick={handleLoginClick} className="btn-primary inline-block">
                            Get Started
                        </button>
                    </div>
                </div>
            </section>

            {/* Integration Carousel Section */}
            <Suspense fallback={<div className="py-8 bg-gray-10" />}>
                <IntegrationCarousel />
            </Suspense>

            {/* Stats Section with Tagline - Below Carousel */}
            <section className="pt-20 relative z-10">
                <div className="container-content">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                        {/* Tagline Text */}
                        <div className="max-w-sm">
                            <h2 className="text-h3 text-gray-90 leading-snug">
                                AI your customers can depend on.
                            </h2>
                            <p className="text-h3 text-gray-90 mt-1">
                                A platform that works with you.
                            </p>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-16">
                            <div className="text-center">
                                <span className="text-h0 text-gray-95 block">75+%</span>
                                <p className="text-paragraph text-gray-95">Call Resolution</p>
                            </div>
                            <div className="text-center">
                                <span className="text-h0 text-gray-95 block">40+</span>
                                <p className="text-paragraph text-gray-95">Integrations</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 1: "Voices that sound human" - Live Demo */}
            <Suspense fallback={<div className="py-16" />}>
                <VoicesSection />
            </Suspense>

            {/* Section 2: "Service backed by data" - Script Showcase */}
            <Suspense fallback={<div className="py-16" />}>
                <ScriptShowcase />
            </Suspense>

            {/* Section 3: "All capability at no code" - Lottie Animation */}
            <Suspense fallback={<div className="py-16" />}>
                <LottieSection />
            </Suspense>

            {/* Final CTA Section */}
            <section className="section-spacing relative z-10">
                <div className="container-content">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        {/* Title */}
                        <h2 className="text-h1 md:text-h0 text-gray-95">
                            Ready to create the voice<br />
                            for your business?
                        </h2>

                        {/* Description and Button */}
                        <div className="flex flex-col items-start md:items-end gap-4">
                            <p className="text-subheading text-gray-80 max-w-sm md:text-right">
                                Syncratik's workflow is simple by design. <br />
                                So you can start to see results, today.
                            </p>
                            <Link to="/contact" className="btn-primary inline-block">
                                Talk to Us
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
